import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import './Login.css';

const LoginRegisterPage = () => {
    const [isRegister, setIsRegister] = useState(false);

    // Obrazec state
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [username, setUsername] = useState('');

    // Statusi
    const [usernameStatus, setUsernameStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // --- NOVO: State za ponovno pošiljanje emaila ---
    const [showResend, setShowResend] = useState(false);
    const [resendStatus, setResendStatus] = useState('');
    // ----------------------------------------------

    // Vidnost gesla
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { loginUser, isAuthenticated } = useContext(AuthContext);

    // --- 1. LOGIKA ZA PREUSMERITEV NAZAJ ---
    const getRedirectPath = () => {
        if (location.state?.from?.pathname) {
            return location.state.from.pathname;
        }
        const storedRedirect = localStorage.getItem('loginRedirect');
        if (storedRedirect) {
            localStorage.removeItem('loginRedirect');
            return storedRedirect;
        }
        return '/';
    };

    // --- 2. USE EFFECT: Preverjanje seje ---
    useEffect(() => {
        if (isAuthenticated) {
            navigate(getRedirectPath());
            return;
        }

        const checkSession = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/session-user`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });

                if (response.ok) {
                    const data = await response.json();
                    loginUser(data.user);
                    navigate(getRedirectPath());
                }
            } catch (err) {
                console.log('No active session found.');
            }
        };

        checkSession();
    }, [isAuthenticated, loginUser, navigate]);

    // --- 3. GOOGLE / FACEBOOK LOGIN LOGIKA ---
    const handleSocialLogin = (provider) => {
        const intendedPath = location.state?.from?.pathname || '/';
        localStorage.setItem('loginRedirect', intendedPath);
        window.location.href = `${process.env.REACT_APP_API_URL}/auth/${provider}`;
    };

    // --- Username check ---
    useEffect(() => {
        if (!isRegister || username.trim().length < 3) {
            setUsernameStatus('idle');
            return;
        }
        const controller = new AbortController();
        const timeout = setTimeout(() => {
            setUsernameStatus('checking');
            fetch(`${process.env.REACT_APP_API_URL}/auth/check-username?username=${encodeURIComponent(username.trim())}`, {
                signal: controller.signal,
            })
                .then(res => res.json())
                .then(data => {
                    setUsernameStatus(data.available ? 'available' : 'taken');
                })
                .catch((err) => {
                    if (err.name !== 'AbortError') setUsernameStatus('idle');
                });
        }, 500);
        return () => {
            clearTimeout(timeout);
            controller.abort();
        };
    }, [username, isRegister]);

    const toggleForm = () => {
        setIsRegister(!isRegister);
        setError(null);
        setSuccess(null);
        setIdentifier('');
        setPassword('');
        setConfirmPassword('');
        setUsername('');
        // --- NOVO: Resetiramo resend state ob menjavi taba ---
        setShowResend(false);
        setResendStatus('');
    };

    // --- NOVO: Funkcija za klic endpointa za ponovno pošiljanje ---
    const handleResendLink = async () => {
        setResendStatus('Pošiljam...');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/resend-verification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // Predvidevamo, da je v 'identifier' vpisal email. 
                // Če je vpisal username, bo backend vrnil "Če račun obstaja...", kar je tudi ok.
                body: JSON.stringify({ email: identifier })
            });

            const data = await response.json();

            if (response.ok) {
                setResendStatus('Povezava poslana! Preverite email.');
                // Po 5 sekundah skrijemo gumb, da ne klikajo preveč
                setTimeout(() => {
                    setShowResend(false);
                    setResendStatus('');
                }, 5000);
            } else {
                setResendStatus(data.message || 'Napaka pri pošiljanju.');
            }
        } catch (err) {
            setResendStatus('Napaka pri povezavi.');
        }
    };
    // -----------------------------------------------------------

    // --- 4. HANDLE SUBMIT ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setShowResend(false); // Resetiramo gumb

        if (isRegister && password !== confirmPassword) {
            setError('Gesli se ne ujemata.');
            return;
        }
        if (isRegister && usernameStatus === 'taken') {
            setError('Uporabniško ime je že zasedeno.');
            return;
        }

        const url = isRegister ? `${process.env.REACT_APP_API_URL}/user/register` : `${process.env.REACT_APP_API_URL}/auth/login`;
        const payload = isRegister
            ? { username, email: identifier, password }
            : { identifier, password };

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include',
            });

            const data = await response.json();

            if (response.ok) {
                if (isRegister) {
                    const intendedPath = location.state?.from?.pathname || '/';
                    localStorage.setItem('loginRedirect', intendedPath);
                    setSuccess('Registracija uspešna! Na vaš email smo poslali potrditveno sporočilo.');
                } else {
                    setSuccess('Uspešna prijava!');
                    loginUser(data.user);
                    navigate(getRedirectPath());
                }
            } else {
                // Obdelava napak
                if (data.field === 'email') setError('Ta email je že v uporabi.');
                else if (data.field === 'username') setError('To uporabniško ime je že zasedeno.');
                else {
                    setError(data.message || 'Prišlo je do napake.');

                    // --- NOVO: Preverimo, če je napaka zaradi nepotrjenega emaila ---
                    // Preverimo, če sporočilo vsebuje ključne besede
                    if (!isRegister && data.message && (
                        data.message.toLowerCase().includes('potrdite email') ||
                        data.message.toLowerCase().includes('potrditev')
                    )) {
                        setShowResend(true);
                    }
                    // -------------------------------------------------------------
                }
            }
        } catch {
            setError('Povezava s strežnikom ni uspela.');
        }
    };

    const calculatePasswordStrength = (password) => { /* ... (nespremenjeno) ... */
        let score = 0;
        if (!password) return { score: 0, text: '', color: '' };
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[a-z]/.test(password)) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;
        let strengthLevel = Math.min(Math.floor(score / 1.5), 3);
        switch (strengthLevel) {
            case 0: return { score: 1, text: 'Šibko', color: '#FF4136' };
            case 1: return { score: 2, text: 'Srednje', color: '#FF851B' };
            case 2: return { score: 3, text: 'Močno', color: '#2ECC40' };
            case 3: return { score: 4, text: 'Zelo močno', color: '#0074D9' };
            default: return { score: 0, text: '', color: '' };
        }
    };

    const passwordStrength = isRegister ? calculatePasswordStrength(password) : { score: 0, text: '' };
    const passwordsMatch = password === confirmPassword;

    return (
        <div className="bg">
            <section className="sign_in_area pt-120 pb-120">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-5 col-md-7 col-sm-9">
                            <div className="sign_in_form">
                                <div className="sign_title">
                                    <h5 className="title">{isRegister ? 'Registracija' : 'Prijava'}</h5>
                                </div>

                                <div className="social-login-buttons mb-4">
                                    <button className="btn btn-social google-btn w-100 mb-2" onClick={() => handleSocialLogin('google')}>
                                        <i className="fab fa-google me-2"></i> Nadaljuj z Google
                                    </button>
                                    <button className="btn btn-social facebook-btn w-100" onClick={() => handleSocialLogin('facebook')}>
                                        <i className="fab fa-facebook-f me-2"></i> Nadaljuj s Facebook
                                    </button>
                                </div>

                                <div className="social-login-divider">
                                    <span className="divider-line"></span>
                                    <span className="divider-text">ALI Z EMAILOM</span>
                                    <span className="divider-line"></span>
                                </div>

                                {error && <div className="alert alert-danger">{error}</div>}

                                {/* --- NOVO: GUMB ZA RESEND --- */}
                                {showResend && !isRegister && (
                                    <div className="alert alert-light text-center border">
                                        <small className="d-block mb-2 text-muted">
                                            Povezava je potekla ali je niste prejeli?
                                        </small>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary w-100"
                                            onClick={handleResendLink}
                                            disabled={resendStatus === 'Pošiljam...'}
                                        >
                                            {resendStatus || 'Pošlji novo aktivacijsko povezavo'}
                                        </button>
                                    </div>
                                )}
                                {/* --------------------------- */}

                                {success && <div className="alert alert-success">{success}</div>}

                                <form onSubmit={handleSubmit}>
                                    <div className="sign_form_wrapper">
                                        {/* ... (OSTALA POLJA KOT PREJ: Username, Email, Password ...) ... */}
                                        {isRegister && (
                                            <div className="single_form_username">
                                                <input type="text" placeholder="Uporabniško ime" value={username} onChange={(e) => setUsername(e.target.value)} required />
                                                <i className="fa fa-user input-icon-left"></i>
                                                {username && (
                                                    <div className="username-status-message" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                                        {usernameStatus === 'checking' && <span style={{ color: '#888' }}>Preverjam...</span>}
                                                        {usernameStatus === 'available' && <span style={{ color: 'green' }}>Na voljo ✅</span>}
                                                        {usernameStatus === 'taken' && <span style={{ color: 'red' }}>Zasedeno ❌</span>}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="single_form">
                                            <input
                                                type="text"
                                                placeholder={isRegister ? "Email" : "Email ali uporabniško ime"}
                                                value={identifier}
                                                onChange={(e) => setIdentifier(e.target.value)}
                                                required
                                            />
                                            <i className="fa fa-envelope input-icon-left"></i>
                                        </div>

                                        <div className="single_form password-input-container">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="Geslo"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                            />
                                            <i className="fa fa-key input-icon-left"></i>
                                            <i
                                                className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon-right`}
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{ cursor: 'pointer' }}
                                            ></i>
                                        </div>

                                        {isRegister && password && (
                                            <div className="password-strength-container">
                                                <div className="password-strength-bar">
                                                    <div className="password-strength-fill" style={{ width: `${(passwordStrength.score / 4) * 100}%`, backgroundColor: passwordStrength.color }}></div>
                                                </div>
                                                <span className="password-strength-text" style={{ color: passwordStrength.color }}>{passwordStrength.text}</span>
                                            </div>
                                        )}

                                        {isRegister && (
                                            <>
                                                <div className="single_form password-input-container">
                                                    <input type={showConfirmPassword ? 'text' : 'password'} placeholder="Potrdi geslo" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                                                    <i className="fa fa-key input-icon-left"></i>
                                                    <i className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon-right`} onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ cursor: 'pointer' }}></i>
                                                </div>
                                                {confirmPassword && (
                                                    <small className={passwordsMatch ? 'text-success' : 'text-danger'}>
                                                        {passwordsMatch ? 'Gesli se ujemata' : 'Gesli se ne ujemata'}
                                                    </small>
                                                )}
                                            </>
                                        )}

                                        <div className="single_form mt-3">
                                            <button type="submit" className="btn btn-success post-item-navbar-btn">
                                                {isRegister ? 'Registracija' : 'Prijava'}
                                            </button>
                                        </div>

                                        <div className="text-center mt-3">
                                            <small>
                                                {isRegister ? 'Že imate račun? ' : "Še nimate računa? "}
                                                <button className="btn btn-link p-0" type="button" onClick={toggleForm} style={{ textDecoration: 'none' }}>
                                                    {isRegister ? 'Prijava' : 'Registracija'}
                                                </button>
                                            </small>
                                        </div>
                                        {!isRegister && (
                                            <div className="text-center mt-2">
                                                <button className="btn btn-link p-0" type="button" onClick={() => navigate('/pozabljeno-geslo')} style={{ textDecoration: 'none', fontSize: '0.9em' }}>
                                                    Pozabljeno geslo?
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LoginRegisterPage;